/**
 * ItemRegistry - Registry for bespoke item definitions
 *
 * Manages item templates with fixed stats per tier. Items have:
 * - name: Display name
 * - slot: Equipment slot (keychain, rearview, dashboard)
 * - stat_type: The stat this item affects
 * - stat_values: Fixed values per tier (faded/standard/pristine/golden/vectorized)
 * - drop_weight: Weighted random selection (higher = more common)
 *
 * @example
 * import { itemRegistry } from './registries/ItemRegistry.js';
 *
 * // Load items from JSON
 * itemRegistry.loadFromJSON(itemsData);
 *
 * // Get a specific item
 * const fuzzyDice = itemRegistry.get('fuzzy_dice');
 *
 * // Get all items for a slot
 * const keychainItems = itemRegistry.getBySlot('keychain');
 *
 * // Get stat value for specific tier
 * const value = itemRegistry.getItemStatValue('fuzzy_dice', 'pristine');
 *
 * // Get weighted random item
 * const randomItem = itemRegistry.getWeightedRandomItem('keychain');
 */
import { Registry } from '../core/Registry.js';
import itemsData from '../data/items/items.json';

// Valid slot types for items
const VALID_SLOT_TYPES = ['keychain', 'rearview', 'dashboard'];

// Valid rarity tiers
const VALID_TIERS = ['faded', 'standard', 'pristine', 'golden', 'vectorized'];

/**
 * Extended Registry for item definitions with item-specific features
 */
export class ItemRegistry extends Registry {
    constructor() {
        super('Items');
        this._config = null;
        this._setupValidators();
    }

    /**
     * Set up item-specific validators
     * @private
     */
    _setupValidators() {
        // Required fields validator
        this.addValidator((id, def) => {
            if (!def.name) return 'Item must have a name';
            // Support both 'slot' (new format) and 'slotType' (legacy)
            const slot = def.slot || def.slotType;
            if (!slot) return 'Item must have a slot';
            // Support both new format (effects array) and legacy (stat_type)
            if (!def.effects && !def.stat_type) return 'Item must have effects array or stat_type';
            if (!def.stat_values) return 'Item must have stat_values';
            if (def.drop_weight === undefined) return 'Item must have a drop_weight';
            return null;
        });

        // Valid slot validator
        this.addValidator((id, def) => {
            const slot = def.slot || def.slotType;
            if (!VALID_SLOT_TYPES.includes(slot)) {
                return `Invalid slot '${slot}'. Must be one of: ${VALID_SLOT_TYPES.join(', ')}`;
            }
            return null;
        });

        // Validate stat_values has all required tiers for each effect
        this.addValidator((id, def) => {
            if (!def.stat_values) return null; // Already caught by required fields

            // New format: effects array with per-stat stat_values
            if (def.effects && Array.isArray(def.effects)) {
                for (const effect of def.effects) {
                    const statValues = def.stat_values[effect.type];
                    if (!statValues) {
                        return `Item stat_values missing values for effect type '${effect.type}'`;
                    }
                    for (const tier of VALID_TIERS) {
                        if (statValues[tier] === undefined) {
                            return `Item stat_values for '${effect.type}' missing tier '${tier}'`;
                        }
                    }
                }
                return null;
            }

            // Legacy format: single stat_type with tier values directly in stat_values
            for (const tier of VALID_TIERS) {
                if (def.stat_values[tier] === undefined) {
                    return `Item stat_values missing tier '${tier}'`;
                }
            }
            return null;
        });
    }

    /**
     * Load items from JSON data
     * @param {Object} data - JSON data with items array and optional config
     * @returns {this} The registry instance for chaining
     */
    loadFromJSON(data) {
        if (!data.items || !Array.isArray(data.items)) {
            throw new Error('[Items] Invalid JSON: missing items array');
        }

        // Store config if provided
        if (data.config) {
            this._config = Object.freeze(data.config);
        }

        // Register each item
        for (const item of data.items) {
            if (!item.id) {
                console.warn('[Items] Skipping item without id:', item);
                continue;
            }
            this.register(item.id, item);
        }

        console.log(`[Items] Loaded ${this.size} items from JSON`);
        return this;
    }

    /**
     * Get all items for a specific slot type
     * @param {string} slotType - Slot type to filter by
     * @returns {Array<Object>} Array of items matching the slot type
     */
    getBySlot(slotType) {
        return this.getAll().filter(item => (item.slot || item.slotType) === slotType);
    }

    /**
     * Get the stat value for a specific item, tier, and optionally stat type
     * @param {string} itemId - Item ID
     * @param {string} tier - Rarity tier (faded, standard, pristine, golden, vectorized)
     * @param {string} [statType] - Optional stat type for multi-stat items
     * @returns {number|null} The stat value for that tier, or null if not found
     */
    getItemStatValue(itemId, tier, statType = null) {
        if (!this.has(itemId)) return null;
        const item = this.get(itemId);
        if (!item.stat_values) return null;

        // New format: stat_values is { statType: { tier: value } }
        if (item.effects && Array.isArray(item.effects)) {
            if (statType) {
                return item.stat_values[statType]?.[tier] ?? null;
            }
            // Return first effect's value if no stat type specified
            const firstEffect = item.effects[0];
            return item.stat_values[firstEffect.type]?.[tier] ?? null;
        }

        // Legacy format: stat_values is { tier: value }
        return item.stat_values[tier] ?? null;
    }

    /**
     * Get a weighted random item, optionally filtered by slot
     * Uses drop_weight for selection probability
     * @param {string|null} slotType - Optional slot to filter by
     * @returns {Object|null} Randomly selected item, or null if no items
     */
    getWeightedRandomItem(slotType = null) {
        const items = slotType ? this.getBySlot(slotType) : this.getAll();
        if (items.length === 0) return null;

        const totalWeight = items.reduce((sum, item) => sum + (item.drop_weight || 100), 0);
        let random = Math.random() * totalWeight;

        for (const item of items) {
            random -= (item.drop_weight || 100);
            if (random <= 0) return item;
        }

        return items[items.length - 1]; // Fallback
    }

    /**
     * Get the loaded config
     * @returns {Object|null} The config object or null if not loaded
     */
    getConfig() {
        return this._config;
    }

    /**
     * Get list of valid slot types
     * @returns {string[]} Array of valid slot type names
     */
    getValidSlotTypes() {
        return [...VALID_SLOT_TYPES];
    }
}

// Singleton instance for app-wide use
export const itemRegistry = new ItemRegistry();

// Auto-load bespoke items on module initialization
itemRegistry.loadFromJSON(itemsData);
